import Constants from '@/Constants';
import { AttributeType, ComparisonMethod, GroupRule, GroupRuleItem, InputValueType } from '@/models/Group';
import http from '@/services/HttpService';
import { ApplicationState } from '@/store';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import './style.scss';

const schema = Yup.object().shape({
    rules: Yup.array().of(
        Yup.object({
            name: Yup.string()
                .min(1)
                .required(),
            items: Yup.array().of(
                Yup.object({
                    value: Yup.string()
                        .min(1)
                        .required(),
                    comparisonMethod: Yup.mixed<ComparisonMethod>()
                        .oneOf(Object.values(ComparisonMethod))
                        .required(),
                    attributeName: Yup.mixed<InputValueType>()
                        .oneOf(Object.values(InputValueType))
                        .required(),
                }),
            ),
        }),
    ),
});

interface Props {
    groupName: string;
    groupId: number | undefined;
    groupRule: GroupRule[] | undefined;
}

export const SelectCondition = ({ groupName, groupId, groupRule }: Props) => {
    const history = useHistory();

    const appId: number = useSelector((state: ApplicationState) => state.identity.data.user.applicationId);
    const orgId: number = useSelector((state: ApplicationState) => state.identity.data.user.orgId);
    const { strings } = useSelector((state: ApplicationState) => ({ strings: state.locale.data }));

    const [rules, setRules] = useState<GroupRule[]>([]);
    const [success, setSuccess] = useState<boolean | null>(null);
    const [isEditRule, setIsEditRule] = useState<boolean>(false);
    const [error, setError] = useState<boolean>(true);

    async function verifyRule() {
        try {
            await schema.validate({ rules });
            if (groupName.length > 0) {
                setError(false);
            } else {
                setError(true);
            }
        } catch (err) {
            setError(true);
        }
    }

    useEffect(() => {
        verifyRule();
    }, [rules, groupName.length]);

    const onPressSendHandler = async (e: React.FormEvent): Promise<void> => {
        e.preventDefault();

        if (isEditRule) {
            http.put(`${Constants.GROUPS_RESULT_URI}/${groupId}`, {
                id: groupId,
                name: groupName,
                applicationId: appId,
                organizationId: orgId,
                rules,
            })
                .then(() => {
                    toast.success(strings.GROUP_EDIT_SUCCESS);
                    setSuccess(true);
                })
                .catch(() => {
                    toast.error(strings.GROUP_EDIT_ERROR);
                    setSuccess(false);
                });
        } else {
            http.post(Constants.GROUPS_RESULT_URI, { name: groupName, applicationId: appId, organizationId: orgId, rules })
                .then(() => {
                    toast.success(strings.CREATE_GROUP_SUCCESS);
                    setSuccess(true);
                })
                .catch(() => {
                    toast.error(strings.CREATE_GROUP_ERROR);
                    setSuccess(false);
                });
        }
    };

    const onMountRuleEdit = (): void => {
        if (groupRule) {
            setIsEditRule(true);
            setRules(groupRule);
        }
    };

    const addRule = (): void => {
        setRules((prevRule) => {
            return [
                ...prevRule,
                {
                    ruleId: prevRule.length + 1,
                    name: '',
                    groupId: isEditRule && groupId ? groupId : 1,
                    items: [
                        {
                            itemId: prevRule.length + 1,
                            attributeName: InputValueType.EMAIL,
                            value: '',
                            attributeType: AttributeType.STATIC,
                            comparisonMethod: ComparisonMethod.EXACT_MATCH,
                            groupRuleId: isEditRule && groupId ? groupId : prevRule.length + 1,
                        },
                    ],
                },
            ];
        });
    };

    const removeRule = (rule: GroupRule): void => {
        setRules((prevRules) => {
            prevRules.splice(prevRules.indexOf(rule), 1);
            return [...prevRules];
        });
    };

    const addConditionRule = (rule: GroupRule): void => {
        setRules((prevRules) => {
            rule.items.push({
                itemId: rule.items.length + 1,
                attributeName: InputValueType.EMAIL,
                value: '',
                attributeType: AttributeType.STATIC,
                comparisonMethod: ComparisonMethod.EXACT_MATCH,
                groupRuleId: rule.id as number,
            });
            return [...prevRules];
        });
    };

    const removeConditionRule = (rule: GroupRule, item: GroupRuleItem): void => {
        setRules((prevRule) => {
            rule.items.splice(rule.items.indexOf(item), 1);
            return [...prevRule];
        });
    };

    const onChangeRuleName = (rule: GroupRule, value: string): void => {
        setRules((prevRule) => {
            rule.name = value;
            return [...prevRule];
        });
    };

    const onChangeRuleCondition = (rule: GroupRule, item: GroupRuleItem): void => {
        setRules((prevRule) => {
            const tmpItem = rule.items.find((x) => (item.id ? x.id === item.id : x.itemId === item.itemId)) as GroupRuleItem;
            Object.assign(tmpItem, item);
            return [...prevRule];
        });
    };

    useEffect(() => addRule(), []);

    useEffect(() => onMountRuleEdit(), [groupRule?.length]);

    useEffect(() => {
        if (success) {
            return history.push('/people');
        }
    }, [success]);

    const goBackToPeople = (): void => history.push('/people');

    return (
        <div className="select-conditional">
            <div>
                {rules.map((rule, index) => (
                    <div key={rule.ruleId}>
                        <div>
                            {index > 0 && (
                                <span>
                                    {strings.OR}
                                    <hr className="separator" />
                                </span>
                            )}
                            <span className="rule">{strings.RULE}</span>
                            <input
                                className="form-control input-rule"
                                name="name"
                                onChange={(e) => onChangeRuleName(rule, e.target.value)}
                                placeholder={strings.TARGET_NAME}
                                required
                                type="text"
                                value={rule.name}
                            />
                            <span className="condition">{strings.CONDITION}</span>

                            {rules.length > 1 && (
                                <button className="delete-rule" onClick={() => removeRule(rule)}>
                                    <i className="material-icons-outlined align-middle">{Constants.icons.DELETE}</i>
                                </button>
                            )}
                            {rule.items.map((item, index) => (
                                <div key={item.itemId}>
                                    <span className="and">{index > 0 && <>{strings.AND}</>}</span>
                                    <div className="d-flex container-option">
                                        <div className="options">
                                            <div className="col-md-2">
                                                <select
                                                    className="form-control type"
                                                    name="type"
                                                    onChange={(e) =>
                                                        onChangeRuleCondition(rule, { ...item, attributeName: e.target.value as InputValueType })
                                                    }
                                                    value={item ? item.attributeName : ''}
                                                >
                                                    <option value="Campo">{strings.INPUT}</option>
                                                    <option selected value={InputValueType.FIRST_NAME}>
                                                        {strings.FIRST_NAME}
                                                    </option>
                                                    <option value={InputValueType.LAST_NAME}>{strings.LAST_NAME}</option>
                                                    <option value={InputValueType.EMAIL}>{strings.EMAIL}</option>
                                                    <option value={InputValueType.DEPARTAMENT_NAME}>{strings.DEPARTAMENT_NAME}</option>
                                                    <option value={InputValueType.POSITION}>{strings.POSITION}</option>
                                                </select>
                                            </div>
                                            <div className="col-md-2 criteria">
                                                <select
                                                    className="form-control criteria"
                                                    name="criteria"
                                                    onChange={(e) =>
                                                        onChangeRuleCondition(rule, {
                                                            ...item,
                                                            comparisonMethod: e.target.value as ComparisonMethod,
                                                        })
                                                    }
                                                    value={item ? item.comparisonMethod : ''}
                                                >
                                                    <option value={strings.CRITERIA}>{strings.CRITERIA}</option>
                                                    <option selected value={ComparisonMethod.EXACT_MATCH}>
                                                        {strings.EQUAL}
                                                    </option>
                                                    <option value={ComparisonMethod.CONTAINS}>{strings.CONTAINS}</option>
                                                </select>
                                            </div>
                                            <div className="col-md-6">
                                                <input
                                                    className="form-control input-text"
                                                    onChange={(e) => onChangeRuleCondition(rule, { ...item, value: e.target.value })}
                                                    placeholder="Nome"
                                                    type="text"
                                                    value={item.value}
                                                />
                                                {rule.items.length > 1 && (
                                                    <button className="button-delete-rule" onClick={() => removeConditionRule(rule, item)}>
                                                        <i className="material-icons-outlined align-middle">{Constants.icons.DELETE}</i>
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <button className="btn btn-light btn-sm button" onClick={() => addConditionRule(rule)}>
                            {strings.ADD_CONDITION}
                        </button>
                    </div>
                ))}
                <div className="button-container">
                    <button className="btn btn-light btn-sm button" onClick={addRule}>
                        {strings.CREATE_RULE}
                    </button>
                </div>
                <hr className="separator" />
                <div className="container-alert">
                    <span className="text-alert">{strings.EDIT_MADE_TO_RULE_CAN_ENROLL_NEW_USERS_BUT_DOES_NOT_ENENROLL_ENROLLED_USERS}</span>
                </div>
                <div className="container-button-buttom">
                    <button className="btn btn-light btn-sm button-cancel" onClick={goBackToPeople}>
                        {strings.CANCEL}
                    </button>
                    <button className="btn btn-primary btn-sm button-save" disabled={error} onClick={onPressSendHandler}>
                        {strings.SAVE}
                    </button>
                </div>
            </div>
        </div>
    );
};
